import React, { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import ModalShell from '../shared/ModalShell';
import {
  PiCheckCircleLight,
  PiBookOpenTextLight,
  PiUserLight,
  PiBuildingsLight,
  PiGlobeLight,
  PiSpinnerGapLight,
  PiWarningCircleLight,
  PiShieldCheckLight,
} from 'react-icons/pi';
import Badge from '../shared/TailwindComponents/Badge';
import Button from '../shared/TailwindComponents/Button';
import SearchInput from '../shared/TailwindComponents/SearchInput';
import apis from '../../utils/apis';
import { PlaybookListItem } from '../../types/playbook';
import Spinner from '../shared/TailwindComponents/Spinner';
import PersonCell from '../shared/access/PersonCell';
import { ownerOf } from '../shared/access/accessModel';

// ─── Risk weight color coding ────────────────────────────────────────────────

export const getRiskWeightColor = (weight: number | null | undefined): string => {
  if (weight == null) return 'bg-gray-100 dark:bg-gray-700 text-gray-500';
  if (weight >= 9) return 'bg-danger-100 dark:bg-danger-900/30 text-danger-700 dark:text-danger-400';
  if (weight >= 6) return 'bg-warning-100 dark:bg-warning-900/30 text-warning-700 dark:text-warning-400';
  if (weight >= 3) return 'bg-warning-100 dark:bg-warning-900/30 text-warning-700 dark:text-warning-400';
  return 'bg-info-100 dark:bg-info-900/30 text-info-700 dark:text-info-400';
};

export const getRiskWeightLabel = (weight: number | null | undefined): string => {
  if (weight == null) return '';
  if (weight >= 9) return 'Critical';
  if (weight >= 6) return 'High';
  if (weight >= 3) return 'Medium';
  return 'Low';
};

// ─── Props ───────────────────────────────────────────────────────────────────

interface PlaybookPickerModalProps {
  selectedPlaybookUuid: string;
  onSelect: (playbook: PlaybookListItem | null) => void;
  onClose: () => void;
}

type FilterTab = 'all' | 'mine' | 'firm' | 'global';

// ─── Component ───────────────────────────────────────────────────────────────

const PlaybookPickerModal: React.FC<PlaybookPickerModalProps> = ({
  selectedPlaybookUuid,
  onSelect,
  onClose,
}) => {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [activeFilter, setActiveFilter] = useState<FilterTab>('all');
  const [pendingUuid, setPendingUuid] = useState<string>(selectedPlaybookUuid);

  const [playbooks, setPlaybooks] = useState<PlaybookListItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Fetch playbooks
  useEffect(() => {
    const load = async () => {
      setIsLoading(true);
      setLoadError(null);
      try {
        // Load ALL playbooks (search here is client-side, so anything beyond the
        // first page would otherwise be unsearchable). Page through until a
        // short/empty page, with a hard safety cap.
        const PAGE_SIZE = 100;
        const all: PlaybookListItem[] = [];
        for (let page = 1; page <= 50; page += 1) {
          const response = await apis.get('/playbooks', {
            params: { page, page_size: PAGE_SIZE },
          });
          if (response.status !== 200) break;
          const items = response.data.items || [];
          all.push(...items);
          const hasNext = response.data?.pagination?.has_next;
          if (hasNext === false || items.length < PAGE_SIZE) break;
        }
        setPlaybooks(all);
      } catch {
        setLoadError(t('Failed to load playbooks. Please try again.'));
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Counts per filter tab
  const counts = useMemo(() => {
    return {
      all: playbooks.length,
      global: playbooks.filter((p) => p.is_global).length,
      firm: playbooks.filter((p) => !p.is_global).length,
      mine: playbooks.filter((p) => !p.is_global && p.has_access?.length > 0).length,
    };
  }, [playbooks]);

  // Apply filter + search
  const filtered = useMemo(() => {
    let list = playbooks;

    if (activeFilter === 'global') list = list.filter((p) => p.is_global);
    else if (activeFilter === 'firm') list = list.filter((p) => !p.is_global);
    else if (activeFilter === 'mine') list = list.filter((p) => !p.is_global && p.has_access?.length > 0);

    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(
        (p) =>
          p.name.toLowerCase().includes(q) ||
          p.description?.toLowerCase().includes(q) ||
          p.agreement_types?.some((a) => a.toLowerCase().includes(q)) ||
          p.jurisdictions?.some((j) => j.toLowerCase().includes(q))
      );
    }

    return list;
  }, [playbooks, activeFilter, search]);

  const handleCardClick = (uuid: string) => {
    setPendingUuid(uuid === pendingUuid ? '' : uuid);
  };

  const handleConfirm = () => {
    const selected = playbooks.find((p) => p.uuid === pendingUuid) || null;
    onSelect(selected);
    onClose();
  };

  const filterTabs: { key: FilterTab; label: string; icon: React.ReactNode; count: number }[] = [
    { key: 'all', label: t('All'), icon: <PiBookOpenTextLight className="w-4 h-4" />, count: counts.all },
    { key: 'mine', label: t('Shared with me'), icon: <PiUserLight className="w-4 h-4" />, count: counts.mine },
    { key: 'firm', label: t('Firm'), icon: <PiBuildingsLight className="w-4 h-4" />, count: counts.firm },
    { key: 'global', label: t('PONS'), icon: <PiGlobeLight className="w-4 h-4" />, count: counts.global },
  ];

  return (
    <ModalShell
      isOpen
      onClose={onClose}
      title={t('Select playbook')}
      description={t('Choose a playbook to run compliance checks against each document')}
      icon={<PiShieldCheckLight />}
      size="2xl"
      height="tall"
      bodyClassName="flex flex-col p-0 overflow-hidden"
      zIndexClassName="z-[300000000]"
      footer={
        <div className="flex w-full items-center justify-between gap-3">
          <p className="text-xs text-fg-muted">
              {filtered.length} {t('playbooks available')}
              {pendingUuid && (
                <span className="ms-2 text-fg-strong font-medium">
                  - 1 {t('selected')}
                </span>
              )}
            </p>
            <div className="flex items-center gap-2">
              {pendingUuid && (
                <Button
                  type="button"
                  variant="outline"
                  size="small"
                  onClick={() => {
                    onSelect(null);
                    onClose();
                  }}
                >
                  {t('Clear selection')}
                </Button>
              )}
              <Button
                type="button"
                variant="primary"
                size="small"
                onClick={handleConfirm}
                disabled={!pendingUuid}
              >
                {t('Confirm')}
              </Button>
            </div>
        </div>
      }
    >
          {/* Body: sidebar + content */}
          <div className="flex flex-1 overflow-hidden">
            {/* Left sidebar - filters */}
            <div className="w-60 flex-shrink-0 border-e border-hairline-soft bg-surface-sunken/40 p-4 flex flex-col gap-1">
              <p className="text-[11px] text-fg-muted font-medium mb-2 px-2">
                {t('Filter by')}
              </p>
              {filterTabs.map((tab) => (
                <button
                  type="button"
                  key={tab.key}
                  onClick={() => setActiveFilter(tab.key)}
                  className={`flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors w-full text-start ${
                    activeFilter === tab.key
                      ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
                      : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700/50'
                  }`}
                >
                  {tab.icon}
                  {/* One line: "Shared with me" wrapped in a 14rem rail. */}
                  <span className="flex-1 min-w-0 truncate whitespace-nowrap">{tab.label}</span>
                  <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                    activeFilter === tab.key
                      ? 'bg-primary-100 dark:bg-primary-800/50 text-primary-600 dark:text-primary-400'
                      : 'bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
                  }`}>
                    {tab.count}
                  </span>
                </button>
              ))}
            </div>

            {/* Right content */}
            <div className="flex-1 flex flex-col overflow-hidden">
              {/* Search bar */}
              <div className="px-5 py-3 border-b border-gray-100 dark:border-gray-800">
                <SearchInput
                  value={search}
                  onChange={setSearch}
                  placeholder={t('Search playbooks...')}
                />
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto px-5 py-4">
                {isLoading ? (
                  <div className="flex items-center justify-center h-32">
                    <Spinner size="glyph" className="!h-6 !w-6" />
                  </div>
                ) : loadError ? (
                  <div className="text-center py-16">
                    <PiWarningCircleLight className="w-12 h-12 text-danger-300 dark:text-danger-700 mx-auto mb-3" />
                    <p className="text-sm text-danger-500 dark:text-danger-400">{loadError}</p>
                  </div>
                ) : filtered.length === 0 ? (
                  <div className="text-center py-16">
                    <PiBookOpenTextLight className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {search ? t('No playbooks match your search') : t('No playbooks available')}
                    </p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                    {filtered.map((pb) => {
                      const isSelected = pb.uuid === pendingUuid;
                      return (
                        <motion.div
                          key={pb.uuid}
                          initial={{ opacity: 0, y: 8 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.15 }}
                          onClick={() => handleCardClick(pb.uuid)}
                          /* Tidy cards (feedback round 3): brand tokens, one
                             hairline border, the champagne accent for the
                             selected one - not a 2px primary frame on white. */
                          className={`relative cursor-pointer rounded-lg border p-4 transition-all hover:shadow-md ${
                            isSelected
                              ? 'border-champagne/60 bg-champagne/5 ring-1 ring-champagne/30 shadow-sm'
                              : 'border-hairline bg-surface hover:border-champagne/40'
                          }`}
                        >
                          {/* Selected checkmark */}
                          {isSelected && (
                            <div className="absolute top-3 end-3">
                              <PiCheckCircleLight className="w-5 h-5 text-champagne" />
                            </div>
                          )}

                          {/* Playbook header */}
                          <div className="flex items-start gap-3 mb-3">
                            <div className="p-2 rounded-lg flex-shrink-0 bg-surface-sunken">
                              <PiShieldCheckLight className={`w-4 h-4 ${isSelected ? 'text-champagne' : 'text-fg-muted'}`} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <h3 className="text-sm font-medium text-fg-strong truncate pe-6">
                                {pb.name}
                              </h3>
                              {pb.description && (
                                <p className="text-xs text-fg-muted mt-0.5 line-clamp-2">
                                  {pb.description}
                                </p>
                              )}
                              <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                                <Badge color={isSelected ? 'blue' : 'gray'} className="text-[10px]">
                                  {pb.clause_count} {t('clauses')}
                                </Badge>
                                {pb.is_global ? (
                                  <Badge color="purple" className="text-[10px]">
                                    {t('By PONS')}
                                  </Badge>
                                ) : (
                                  /*
                                    THE CREATOR AS A PERSON, not a text badge.

                                    This drew `owner.name` inside a Badge, so
                                    the picture on `PlaybookSharedUser.
                                    profile_picture` was never asked for - the
                                    same playbook showed a face in the
                                    templates list and a blue pill here.

                                    It also fell back to `has_access[0]` when
                                    no entry claimed admin, which credits a
                                    shared playbook to whoever the backend
                                    listed first. `ownerOf` returns undefined
                                    instead, and PersonCell then draws nothing
                                    at all rather than a guess.
                                  */
                                  (() => {
                                    const owner = ownerOf(pb.has_access);
                                    return owner ? (
                                      <PersonCell person={owner} size="xs" />
                                    ) : null;
                                  })()
                                )}
                                {pb.agreement_types?.slice(0, 2).map((type) => (
                                  <Badge key={type} color="gray" className="text-[10px] capitalize">
                                    {type.replace(/_/g, ' ')}
                                  </Badge>
                                ))}
                              </div>
                            </div>
                          </div>

                          {/* Jurisdiction tags */}
                          {pb.jurisdictions && pb.jurisdictions.length > 0 && (
                            <div className="flex items-center gap-1.5 mb-3 flex-wrap">
                              {pb.jurisdictions.slice(0, 3).map((j) => (
                                <span
                                  key={j}
                                  className="text-[10px] px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 font-medium"
                                >
                                  {j}
                                </span>
                              ))}
                              {pb.jurisdictions.length > 3 && (
                                <span className="text-[10px] text-gray-400">
                                  +{pb.jurisdictions.length - 3} {t('more')}
                                </span>
                              )}
                            </div>
                          )}

                        </motion.div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
    </ModalShell>
  );
};

export default PlaybookPickerModal;
